import { Action } from 'redux-actions';
import {
  all,
  AllEffect,
  call,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeEvery,
  takeLatest
  } from 'redux-saga/effects';
import { getChartData } from '../../chart/api';
import {
  addComparison,
  changingChartData,
  draw,
  drawingsChanged,
  layoutChanged,
  redid,
  removeComparison,
  saveLayout,
  setChartType,
  setPeriodicity,
  setSymbol,
  toggleCrosshairs,
  undid
  } from './actions';
import {
  CHARTIQ_ADD_COMPARISON_AND_SAVE,
  CHARTIQ_CHANGE_DRAWINGS,
  CHARTIQ_CLEAR,
  CHARTIQ_FETCH_DATA,
  CHARTIQ_LAYOUT_CHANGED_AND_SAVE,
  CHARTIQ_REDO,
  CHARTIQ_REMOVE_COMPARISON_AND_SAVE,
  CHARTIQ_SET_CHART_TYPE_AND_SAVE,
  CHARTIQ_SET_PERIODICITY_WITH_LOADER,
  CHARTIQ_SET_SPAN_WITH_LOADER,
  CHARTIQ_SET_SYMBOL_AND_SAVE,
  CHARTIQ_SET_TIME_ZONE,
  CHARTIQ_TOGGLE_CROSSHAIRS_AND_SAVE,
  CHARTIQ_UNDO
  } from './constants';

const CIQ = (window as any).CIQ;

interface IFetchDataPayload {
  ciq: any
  cb: (data: any) => void
  symbol: string
  token: string
  interval: string
  startDate: Date
  endDate: Date
}

function* fetchData({ payload }: any) {
  const { cb, symbol, interval, startDate, endDate } = payload as IFetchDataPayload;
  const startEpoch = Math.floor(startDate.getTime() / 1000);
  const endEpoch = Math.floor(endDate.getTime() / 1000);
  const response: {}[] = yield call(getChartData, `charts?symbol=${symbol}&interval=${interval}&stop=${endEpoch}&start=${startEpoch}&limit=1000`) as any;
  cb(
    {
    moreAvailable: response.length > 1,
    quotes: response.reverse().map((olhcData: any) => {

      return {
        Close: olhcData.close,
        Date: olhcData.date,
        High: olhcData.high,
        Low: olhcData.low,
        Open: olhcData.open,
        Percentile10: parseFloat(olhcData.percentile10),
        Percentile90: parseFloat(olhcData.percentile90),
        Rvol: olhcData.rvol,
        Rvolmin: olhcData.rvolmin,
        Signal10_action: olhcData.signal10_action,
        Signal90_action: olhcData.signal90_action,
        Tftf10_open: parseFloat(olhcData.tftf10_open),
        Tftf90_open: parseFloat(olhcData.tftf90_open),
        Vix: olhcData.vix,
      };
    }),
  });
}

function* setPeriodicityWithLoader({ payload }: any) {
  const { ciq, periodicity } = payload;
  let loadRange = false
  let dtLeft
  let dtRight
  if (ciq.isHistoricalModeSet) {
    // save the range and reload it
    const dataSegment = ciq.chart.dataSegment;
    if (dataSegment && dataSegment.length > 0) {
      loadRange = true
      if (dataSegment[0]) {
        dtLeft = dataSegment[0].DT
      }
      if (dataSegment[dataSegment.length - 1]) {
        dtRight = dataSegment[dataSegment.length - 1].DT
      }
    }
  }
  yield put(changingChartData(true));
  yield new Promise<void>(resolve => {
    ciq.setPeriodicity(periodicity, () => {
      resolve();
    });
  });
  yield all([put(setPeriodicity(payload)), put(changingChartData(false))]);
  if (loadRange) {
    ciq.setRange({
      dtLeft,
      dtRight,
      forceLoad: true,
      periodicity,
    });
  }
}

function* setSymbolAndSave({ payload }: any): Generator<Promise<void> | AllEffect<any> | PutEffect<Action<any>>> {
  const { ciq, symbol } = payload;
  if (ciq) {
    yield new Promise<void>(resolve => {
      ciq.newChart(symbol, null, null, resolve);
    });
    yield all([
      yield put(setSymbol(payload)),
      yield put(saveLayout(payload))
    ]);
  }
}

function* setSpanWithLoader(params: any) {
  const { ciq, multiplier, base, interval, period, timeUnit } = params.payload;
  const periodicity: any = {
    base,
    multiplier,
  };

  if (interval) {
    periodicity.periodicity = {
      interval,
      period: period || 1,
      timeUnit
    };
  }
  yield put(changingChartData(true));
  yield new Promise<void>(resolve => {
    ciq.setSpan(periodicity, resolve);
  });
  yield all([
    put(changingChartData(false)),
    put(
      setPeriodicity({
        interval: ciq.layout.interval,
        period: ciq.layout.period,
        timeUnit: ciq.layout.timeUnit
      })
    )
  ]);
}

function* triggerCrosshairsAndSave(params: any) {
  yield all([put(toggleCrosshairs(params.payload)), put(saveLayout(params.payload))]);
}

function* layoutChangedAndSave(params: any) {
  yield all([put(layoutChanged(params.payload)), put(saveLayout(params.payload))]);
}

function* addComparisonAndSave({ payload }: any) {
  const { ciq, symbol, comparison } = payload;
  const series: Action<any> = yield new Promise<any>((resolve, reject) => {
    ciq.addSeries(symbol, comparison, (err: any, s: any) => {
      if (err) reject(err);
      if (!err) resolve(s);
    });
  });
  yield all([put(addComparison({ ciq, series })), put(saveLayout(payload))]);
}

function* removeComparisonAndSave(params: any) {
  yield all([put(removeComparison(params.payload)), put(saveLayout(params.payload))]);
}

function* changeDrawings({ payload }: any) {
  const { ciq } = payload;
  const state: SelectEffect = yield select();
  const chart = (state as any).get('chartiq').toJS();
  const tmp = ciq.exportDrawings();
  if (tmp.length > 0) {
    CIQ.localStorage.removeItem(chart.symbol);
  } else {
    CIQ.localStorageSetItem(chart.symbol, JSON.stringify(tmp));
  }

  yield put(drawingsChanged(payload));
}

function* redo({ payload }: any) {
  const { ciq } = payload;
  const state: SelectEffect = yield select();
  const chart = (state as any).get('chartiq').toJS();
  const redone = chart.redoStack.pop();
  if (redone) {
    const drawings = CIQ.shallowClone(redone);
    const oldDrawings = CIQ.shallowClone(ciq.drawingObjects);
    ciq.drawingObjects = drawings;
    yield all([put(redid(oldDrawings)), put(draw({ ciq }))]);
  }
}

// eslint-disable-next-line require-yield
function* setTimezone(params: any): any {
  throw new Error('not implemented');
}

function* undo({ payload }: any) {
  const { ciq } = payload;
  const state: SelectEffect = yield select();
  const chart = (state as any).get('chartiq').toJS();
  const undone = chart.undoStack.pop();
  if (undone) {
    const drawings = CIQ.shallowClone(undone);
    const oldDrawings = CIQ.shallowClone(ciq.drawingObjects);
    ciq.drawingObjects = drawings;
    yield all([put(undid(oldDrawings)), put(draw({ ciq }))]);
  }
}

function* setChartTypeAndSave({ payload }: any) {
  const { ciq, chartType } = payload;

  if (chartType.aggregationEdit && ciq.layout.aggregationType !== chartType.type) {
    ciq.setChartType('none');
    ciq.setAggregationType(chartType.type);
  } else {
    ciq.setAggregationType(chartType.type);
    ciq.setChartType(chartType.type);
  }

  ciq.draw();

  yield put(setChartType(payload));
}

function clear({ payload }: any) {
  const { ciq } = payload;
  ciq.clearDrawings();
}

export function* sagaWatcher() {
  yield takeEvery(CHARTIQ_SET_PERIODICITY_WITH_LOADER, setPeriodicityWithLoader);
  yield takeEvery(CHARTIQ_SET_SPAN_WITH_LOADER, setSpanWithLoader);
  yield takeEvery(CHARTIQ_SET_SYMBOL_AND_SAVE, setSymbolAndSave);
  yield takeEvery(CHARTIQ_TOGGLE_CROSSHAIRS_AND_SAVE, triggerCrosshairsAndSave);
  yield takeEvery(CHARTIQ_LAYOUT_CHANGED_AND_SAVE, layoutChangedAndSave);
  yield takeEvery(CHARTIQ_ADD_COMPARISON_AND_SAVE, addComparisonAndSave);
  yield takeEvery(CHARTIQ_REMOVE_COMPARISON_AND_SAVE, removeComparisonAndSave);
  yield takeEvery(CHARTIQ_CHANGE_DRAWINGS, changeDrawings);
  yield takeEvery(CHARTIQ_REDO, redo);
  yield takeEvery(CHARTIQ_UNDO, undo);
  yield takeEvery(CHARTIQ_SET_TIME_ZONE, setTimezone);
  yield takeLatest(CHARTIQ_SET_CHART_TYPE_AND_SAVE, setChartTypeAndSave);
  yield takeLatest(CHARTIQ_CLEAR, clear);
  yield takeLatest(CHARTIQ_FETCH_DATA, fetchData);
}

const gens = [sagaWatcher()];
export default gens;
